import React, { useState } from "react"
import {
  Flex, Stack, Text, Divider,
} from "@chakra-ui/react"
import * as Yup from "yup"
import Button from "components/Buttons/Button"
import ContainedForm from "components/ReactHookForm/ContainedForm"
import Menu, { MenuItem } from "components/dialogs/Menu"
import { useParams } from "react-router-dom"
import ConfirmDelete from "components/dialogs/Confirm/ConfirmDelete"
import {
  Account, DioboxEvent, ModalName, TextMessage,
} from "sharedTypes"
import * as api from "api/texts"
import { putEvent } from "api/events"
import { handleError, successToast } from "utilities/toasts"
import useModal from "services/useModal"
import { useQueryClient } from "react-query"
import {
  refetchTextMessages, setCurrentEvent, updateSmsCreditsInAccount, updateTextMessage,
} from "context/actions"
import { useCurrentUser } from "queries/users"
import {
  TEXT_MESSAGE_BODY_CHARACTER_LIMIT,
} from "pages/EventShow/Texts/SelectedTextMessage/SelectedTextMessage"
import useWhatsAppTemplate from "services/useWhatsAppTemplate"
import useDefaultOptInText from "services/useDefaultOptInText"
import { hasQRCodeConflict, isWhatsappTextMessageConfigured } from "utilities/whatsappTemplates"
import { useForms } from "queries/forms"
import OptInInfoBox from "../OptInInfoBox"
import FormContent from "../FormContent/FormContent"
import TextUpgradeAlert from "../../TextUpgradeAlert"
import InsufficientSMSCreditsAlertBox from "./InsufficientSMSCreditsAlertBox"
import SendConfirmationModal from "./SendConfirmationModal"
import OptInTextBody from "./OptInTextBody"

type Props = {
  event: DioboxEvent
  selectedTextMessage: TextMessage
  onSave: (textMessage: TextMessage, options?: { silent?: boolean }) => void
  onDelete: () => void
  showSavedLabel: boolean
  onDuplicate: (textMessage: TextMessage) => void
  enabled: boolean
  emailEnabled: boolean
  smsCredits: number
  currentAccount: Account
  onClickAddCredits: () => void
}

// WhatsApp: validate template variables are filled
// SMS: validate body is present and within character limit
const buildValidationSchema = (channel: string, isTemplateBased: boolean) => {
  if (channel === "whatsapp" && isTemplateBased) {
    return Yup.object().shape({
      recipients: Yup.array().min(1),
      whatsappContentVariables: Yup.object().test(
        "all-filled",
        "All variable fields must be filled",
        (val) => Boolean(val)
          && Object.values(val as Record<string, string>)
            .every((v) => v?.trim().length > 0),
      ),
    })
  }

  return Yup.object().shape({
    body: Yup.string()
      .required()
      .max(TEXT_MESSAGE_BODY_CHARACTER_LIMIT)
      .test(
        "no-empty-form-link",
        "Please select a form for all form link placeholders",
        (val) => !val?.includes("{{FORM_}}"),
      ),
    recipients: Yup.array().min(1),
  })
}

const DraftTextMessage = ({
  event,
  selectedTextMessage: { guestCount, name },
  selectedTextMessage,
  onSave,
  onDelete,
  showSavedLabel,
  onDuplicate,
  enabled,
  emailEnabled,
  smsCredits,
  currentAccount: { id: accountId },
  onClickAddCredits,
}: Props) => {
  const { eventId, textMessageId } = useParams<{ eventId: string, textMessageId: string }>()
  const showModal = useModal()
  const queryClient = useQueryClient()
  const currentUser = useCurrentUser()
  const [showConfirmationModal, setShowConfirmationModal] = useState(false)
  const {
    template, isTemplateBased, templateUnavailable,
  } = useWhatsAppTemplate(selectedTextMessage.whatsappContentSid)
  const { data: formsData } = useForms(eventId)
  const formsList = formsData?.data ?? []

  const temporalQRCodeConflict = hasQRCodeConflict(template, event.expiringQrCodes)
  const isOptInText = template?.messageType === "opt_in"
  const isDefaultOptInText = isOptInText
    && selectedTextMessage.id !== undefined
    && event.defaultOptInTextId === selectedTextMessage.id

  // Auto-opt-in fires only when ALL of:
  //   - event-level flag is on
  //   - a default opt-in TextMessage is set on the event
  //   - that default opt-in TextMessage's content variables are all filled
  // The third check guards against a misconfigured default opt-in (a saved
  // TextMessage with empty required fields). Without it, the FE would gate
  // Send All as if auto-opt-in works while the BE auto-dispatch fails on the
  // missing variables.
  // TODO(BE): defense-in-depth — the BE's send pipeline should also validate
  // that the default opt-in TextMessage's content variables are all filled
  // before attempting to auto-fire. Either reject the send with a clear error,
  // or silently skip the auto-fire (treating misconfigured default the same as
  // no default). Without this, an API client or race condition could bypass
  // the FE check and put the system in the same broken state.
  const { isConfigured: isDefaultOptInConfigured } = useDefaultOptInText(
    eventId,
    event.defaultOptInTextId,
    event.defaultOptInTextId !== null,
  )
  const autoOptInActive = event.autoOptInEnabled
    && event.defaultOptInTextId !== null
    && isDefaultOptInConfigured
  const isNonOptInWhatsApp = selectedTextMessage.channel === "whatsapp" && !isOptInText
  const optedInCount = selectedTextMessage.optedInRecipientCount ?? 0
  const unoptedCount = selectedTextMessage.unoptedRecipientCount ?? 0
  const noNonOptInRecipients = isNonOptInWhatsApp && !autoOptInActive && optedInCount === 0
  const noOptInRecipients = isOptInText && unoptedCount === 0
  const noOneReached = noNonOptInRecipients || noOptInRecipients
  // Credit budget mirrors the BE's send_all_budget logic:
  //   - opt-in template: only unopted (already-opted are silently dropped)
  //   - auto-opt-in active: full audience (each unopted pre-budgets 1 credit
  //     for the opt-in template now; the deferred original is charged at
  //     dispatch on YES)
  //   - auto-opt-in inactive: only opted-in (unopted silently dropped)
  const effectiveBudget = selectedTextMessage.channel !== "whatsapp"
    ? guestCount
    : isOptInText
      ? unoptedCount
      : autoOptInActive
        ? optedInCount + unoptedCount
        : optedInCount

  const validationSchema = buildValidationSchema(selectedTextMessage.channel, isTemplateBased)

  const hasMissingWhatsappVariables = selectedTextMessage.channel === "whatsapp"
    && isTemplateBased
    && !isWhatsappTextMessageConfigured(selectedTextMessage, template)

  const handleSubmit = async (textMessage?: TextMessage) => {
    if (textMessage) {
      await api.putTextMessage(
        eventId, textMessageId, textMessage,
      )
    }

    return api.sendTextMessage(eventId, textMessageId)
      .then(({ data }) => {
        updateTextMessage(queryClient, textMessageId, data)
        updateSmsCreditsInAccount(queryClient, accountId, -effectiveBudget)
        refetchTextMessages(queryClient, eventId)
        setShowConfirmationModal(false)
        successToast({ title: "Text Message sent to all recipients" })
      })
  }

  return (
    <Stack spacing={7}>
      <Flex justifyContent="space-between">
        <Text fontSize="2xl" fontWeight="semibold">
          {name}
        </Text>
        <Flex justifyContent="flex-end" alignItems="center" gap={2}>
          <Button onClick={() => showModal(ModalName.PreviewTextMessage, { textMessage: selectedTextMessage, enabled: enabled && emailEnabled })} variant="outline" size="sm" isDisabled={templateUnavailable || hasMissingWhatsappVariables}>Preview + Send Test</Button>
          <Menu
            noPadding
            buttonText="More"
            menuItems={[
              <MenuItem
                key="rename"
                onClick={() => showModal(ModalName.EditTextMessageName,
                  { onSubmit: onSave, initialValues: selectedTextMessage })}
              >Rename
              </MenuItem>,
              <MenuItem key="duplicate" onClick={() => onDuplicate(selectedTextMessage)}>Duplicate Text</MenuItem>,
              <Divider key="divider" />,
              <ConfirmDelete key="delete" type="Text">
                <MenuItem onClick={onDelete} color="red.700">Delete</MenuItem>
              </ConfirmDelete>,
            ]}
          />
        </Flex>
      </Flex>
      {!enabled && (
        <TextUpgradeAlert />
      )}
      {isOptInText && (
        <OptInInfoBox
          isDefault={isDefaultOptInText}
          onSetAsDefault={async () => {
            if (selectedTextMessage.id === undefined) return
            try {
              const { data } = await putEvent(event.id, {
                ...event,
                defaultOptInTextId: selectedTextMessage.id,
              })
              setCurrentEvent(queryClient, data, currentUser)
            } catch (error) {
              handleError(error)
            }
          }}
        />
      )}
      {isOptInText ? (
        <OptInTextBody
          event={event}
          selectedTextMessage={selectedTextMessage}
          onSave={onSave}
          showSavedLabel={showSavedLabel}
        />
      ) : (
        <ContainedForm
          values={{ ...selectedTextMessage, type: "sms" }}
          onSubmit={() => setShowConfirmationModal(true)}
          submitButtonText="Send All"
          showCancel={false}
          disableSubmitWhenNotDirty={false}
          validationSchema={validationSchema}
          submitIsDisabled={
            !enabled || !emailEnabled || guestCount === 0
            || smsCredits < effectiveBudget || templateUnavailable
            || temporalQRCodeConflict
            || noOneReached
          }
        >
          {({
            control, watch, getValues, formState: { isDirty },
          }) => (
            <>
              <FormContent
                control={control}
                event={event}
                selectedTextMessage={selectedTextMessage}
                handleSave={onSave}
                watch={watch}
                getValues={getValues}
                showSavedLabel={showSavedLabel}
              />
              {showConfirmationModal && (
                <SendConfirmationModal
                  onClose={() => setShowConfirmationModal(false)}
                  onSubmit={() => handleSubmit(isDirty ? getValues() : undefined)}
                  channel={selectedTextMessage.channel}
                  guestCount={guestCount}
                  textMessageName={selectedTextMessage.name}
                  eventId={event.id}
                  optedInRecipientCount={selectedTextMessage.optedInRecipientCount}
                  unoptedRecipientCount={selectedTextMessage.unoptedRecipientCount}
                  autoOptInEnabled={event.autoOptInEnabled}
                  defaultOptInTextId={event.defaultOptInTextId}
                  template={template}
                  whatsappContentVariables={getValues("whatsappContentVariables")}
                  body={getValues("body")}
                  forms={formsList}
                />
              )}
            </>
          )}
        </ContainedForm>
      )}
      {enabled && !isOptInText && (smsCredits < effectiveBudget || smsCredits === 0) && (
        <InsufficientSMSCreditsAlertBox
          onClickAddCredits={onClickAddCredits}
        />
      )}
    </Stack>
  )
}

export default DraftTextMessage
